/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { tasksApi, PRIORITY_COLORS, PRIORITY_LABELS, matchesFilter } from './tasksApi';
import { TaskFilterBar, emptyFilter } from './TaskFilterBar';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

/**
 * Network/dependency graph view. Tasks, people, work areas, big-picture items as nodes.
 * Edges: task→work_area (work_area_id), task→person (task_associated_person_id),
 * task→bp item (big_picture_id).
 *
 * Layout: simple deterministic placement — work areas in left band, people in right band,
 * big-picture items in center, tasks scattered around their primary parent.
 */
export const GraphView = ({ onOpenTask }) => {
  const { workAreaNames, workAreaColors } = useWorkAreas();
  const [tasks, setTasks] = useState([]);
  const [bpItems, setBpItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(emptyFilter());
  const [zoom, setZoom] = useState(0.85);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0 });
  const [hovered, setHovered] = useState(null); // {kind, id}

  const load = async () => {
    setLoading(true);
    try {
      const [t, e, s, p] = await Promise.all([
        tasksApi.listTasks(),
        tasksApi.listEpics(),
        tasksApi.listSprints(),
        tasksApi.listPeople(),
      ]);
      setTasks(t || []);
      setBpItems([...(e || []), ...(s || [])]);
      setPeople(p || []);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => tasks.filter((t) => matchesFilter(t, filter)), [tasks, filter]);

  // Layout
  const layout = useMemo(() => {
    const W = 1600, H = 900;
    const centerX = W / 2, centerY = H / 2;

    // Work areas — left band (vertical column)
    const waEntries = Object.entries(workAreaNames);
    const waNodes = waEntries.map(([id, name], i) => ({
      kind: 'work_area', id, label: name,
      x: 140, y: 100 + (i / Math.max(waEntries.length - 1, 1)) * (H - 200),
      color: workAreaColors[id] || '#94a3b8',
    }));

    // People — right band
    const peopleNodes = people.slice(0, 12).map((p, i) => ({
      kind: 'person', id: p.user_id || p.person_id || p.email,
      label: p.name || p.email,
      x: W - 140, y: 100 + (i / Math.max(Math.min(people.length, 12) - 1, 1)) * (H - 200),
    }));

    // Big-picture (epics + sprints) — center column
    const bpNodes = bpItems.slice(0, 8).map((b, i) => ({
      kind: 'bp', id: b.item_id, label: b.title, type: b.type,
      x: centerX, y: 80 + (i / Math.max(bpItems.length - 1, 1)) * (H - 160),
    }));

    // Tasks — scattered between, grouped near their primary parent
    const taskNodes = filtered.slice(0, 80).map((task, i) => {
      // Try to anchor near big-picture parent if any, else near work_area
      let baseX = centerX, baseY = centerY;
      const bp = bpNodes.find((n) => n.id === task.big_picture_id);
      const wa = waNodes.find((n) => n.id === task.work_area_id);
      if (bp) { baseX = bp.x; baseY = bp.y; }
      else if (wa) { baseX = wa.x + 120; baseY = wa.y; }

      // Distribute around the base point in a small ring
      const ringIdx = filtered.slice(0, i).filter((t) => t.big_picture_id === task.big_picture_id || t.work_area_id === task.work_area_id).length;
      const angle = (ringIdx * 0.6) % (Math.PI * 2);
      const radius = 100 + (ringIdx % 3) * 40;
      return {
        kind: 'task', id: task.task_id, task,
        x: baseX + Math.cos(angle) * radius * (bp ? 1 : 0.7),
        y: baseY + Math.sin(angle) * radius * (bp ? 1 : 0.7),
      };
    });

    // Edges
    const edges = [];
    taskNodes.forEach((tn) => {
      if (tn.task.work_area_id) {
        const w = waNodes.find((n) => n.id === tn.task.work_area_id);
        if (w) edges.push({ from: w, to: tn, color: w.color || '#94a3b8', kind: 'wa' });
      }
      if (tn.task.task_associated_person_id) {
        const p = peopleNodes.find((n) => n.id === tn.task.task_associated_person_id);
        if (p) edges.push({ from: p, to: tn, color: '#60a5fa', kind: 'person' });
      }
      if (tn.task.big_picture_id) {
        const b = bpNodes.find((n) => n.id === tn.task.big_picture_id);
        if (b) edges.push({ from: b, to: tn, color: '#a78bfa', kind: 'bp' });
      }
    });

    return { width: W, height: H, waNodes, peopleNodes, bpNodes, taskNodes, edges };
  }, [filtered, bpItems, people, workAreaNames, workAreaColors]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.max(0.2, Math.min(3, z * delta)));
  };

  const onMouseDown = (e) => {
    dragRef.current = { dragging: true, startX: e.clientX - pan.x, startY: e.clientY - pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setPan({ x: e.clientX - dragRef.current.startX, y: e.clientY - dragRef.current.startY });
  };
  const onMouseUp = () => { dragRef.current.dragging = false; };

  const isHighlighted = (kind, id) => {
    if (!hovered) return true;
    if (hovered.kind === kind && hovered.id === id) return true;
    // Highlight connected
    if (hovered.kind === 'task') {
      const t = filtered.find((x) => x.task_id === hovered.id);
      if (!t) return false;
      if (kind === 'work_area' && t.work_area_id === id) return true;
      if (kind === 'person' && t.task_associated_person_id === id) return true;
      if (kind === 'bp' && t.big_picture_id === id) return true;
    }
    if (hovered.kind === 'work_area') return kind === 'task' && filtered.find((t) => t.task_id === id)?.work_area_id === hovered.id;
    if (hovered.kind === 'person') return kind === 'task' && filtered.find((t) => t.task_id === id)?.task_associated_person_id === hovered.id;
    if (hovered.kind === 'bp') return kind === 'task' && filtered.find((t) => t.task_id === id)?.big_picture_id === hovered.id;
    return false;
  };

  return (
    <div data-testid="graph-view" className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-muted-foreground">Network graph</span>
        <span className="text-[11px] text-muted-foreground">
          {layout.taskNodes.length} tasks · {layout.waNodes.length} work areas · {layout.peopleNodes.length} people · {layout.bpNodes.length} epics/sprints
        </span>
        <div className="flex gap-1 ml-auto">
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setZoom((z) => Math.min(3, z * 1.2))} data-testid="graph-zoom-in"><ZoomIn className="w-3 h-3" /></Button>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setZoom((z) => Math.max(0.2, z / 1.2))} data-testid="graph-zoom-out"><ZoomOut className="w-3 h-3" /></Button>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => { setZoom(0.85); setPan({ x: 0, y: 0 }); }} data-testid="graph-reset"><RotateCcw className="w-3 h-3" /></Button>
        </div>
      </div>

      <TaskFilterBar value={filter} onChange={setFilter} compact />

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading
        </div>
      ) : (
        <Card
          className="bg-slate-50 dark:bg-slate-900 overflow-hidden cursor-grab active:cursor-grabbing"
          style={{ height: 'calc(100vh - 320px)' }}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          data-testid="graph-canvas"
        >
          <svg width="100%" height="100%" viewBox={`0 0 ${layout.width} ${layout.height}`} preserveAspectRatio="xMidYMid meet">
            <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {/* Edges */}
              {layout.edges.map((e, i) => {
                const high = isHighlighted(e.from.kind, e.from.id) && isHighlighted(e.to.kind, e.to.id);
                return (
                  <line key={`e-${i}`} x1={e.from.x} y1={e.from.y} x2={e.to.x} y2={e.to.y}
                    stroke={e.color} strokeWidth={high ? 1.5 : 0.7}
                    strokeOpacity={high ? 0.55 : 0.15} />
                );
              })}

              {/* Work areas (rectangles, left band) */}
              {layout.waNodes.map((n) => (
                <g key={n.id} transform={`translate(${n.x}, ${n.y})`}
                  onMouseEnter={() => setHovered({ kind: 'work_area', id: n.id })}
                  onMouseLeave={() => setHovered(null)}
                  className="cursor-pointer">
                  <rect x="-60" y="-14" width="120" height="28" rx="4"
                    fill={n.color} fillOpacity={isHighlighted('work_area', n.id) ? 0.85 : 0.3}
                    stroke={n.color} strokeWidth="1.5" />
                  <text x="0" y="4" fontSize="11" fontWeight="600" textAnchor="middle"
                    fill="white" pointerEvents="none">{n.label}</text>
                </g>
              ))}

              {/* People (circles, right band) */}
              {layout.peopleNodes.map((n) => (
                <g key={n.id} transform={`translate(${n.x}, ${n.y})`}
                  onMouseEnter={() => setHovered({ kind: 'person', id: n.id })}
                  onMouseLeave={() => setHovered(null)}
                  className="cursor-pointer">
                  <circle r="22" fill="#3b82f6" fillOpacity={isHighlighted('person', n.id) ? 0.9 : 0.3} stroke="#1d4ed8" strokeWidth="1.5" />
                  <text x="0" y="4" fontSize="10" fontWeight="600" textAnchor="middle" fill="white" pointerEvents="none">
                    {(n.label || '').slice(0, 2).toUpperCase()}
                  </text>
                  <text x="0" y="42" fontSize="10" textAnchor="middle"
                    fill="currentColor" className="fill-foreground" pointerEvents="none">
                    {(n.label || '').slice(0, 14)}
                  </text>
                </g>
              ))}

              {/* Big-picture items (diamonds, center) */}
              {layout.bpNodes.map((n) => (
                <g key={n.id} transform={`translate(${n.x}, ${n.y})`}
                  onMouseEnter={() => setHovered({ kind: 'bp', id: n.id })}
                  onMouseLeave={() => setHovered(null)}
                  className="cursor-pointer">
                  <polygon points="-30,0 0,-22 30,0 0,22"
                    fill="#a78bfa" fillOpacity={isHighlighted('bp', n.id) ? 0.9 : 0.4}
                    stroke="#7c3aed" strokeWidth="1.5" />
                  <text x="0" y="4" fontSize="9" fontWeight="700" textAnchor="middle" fill="white" pointerEvents="none">
                    {n.type === 'EPIC' ? 'EPIC' : 'SPRINT'}
                  </text>
                  <text x="0" y="40" fontSize="10" textAnchor="middle"
                    fill="currentColor" className="fill-foreground" pointerEvents="none">
                    {(n.label || '').slice(0, 18)}
                  </text>
                </g>
              ))}

              {/* Tasks (small rounded rectangles) */}
              {layout.taskNodes.map((tn) => (
                <g key={tn.id} transform={`translate(${tn.x}, ${tn.y})`}
                  onClick={() => onOpenTask && onOpenTask(tn.task)}
                  onMouseEnter={() => setHovered({ kind: 'task', id: tn.id })}
                  onMouseLeave={() => setHovered(null)}
                  className="cursor-pointer"
                  data-testid={`graph-task-${tn.id}`}>
                  <rect x="-50" y="-14" width="100" height="28" rx="3"
                    fill="white" className="dark:fill-slate-800"
                    stroke={tn.task.task_status === 'Done' ? '#22c55e' : '#94a3b8'}
                    strokeWidth="1"
                    fillOpacity={isHighlighted('task', tn.id) ? 1 : 0.6} />
                  <text x="0" y="4" fontSize="9" textAnchor="middle"
                    fill="currentColor" className="fill-foreground" pointerEvents="none">
                    {(tn.task.task_title || '').slice(0, 16)}
                  </text>
                </g>
              ))}
            </g>
          </svg>
        </Card>
      )}
    </div>
  );
};

export default GraphView;
